/**
 * The NPC roster: the bank's resident "bank sitters" declared as data.
 *
 * Like {@link content} this is **content-as-data** - a flat list of plain,
 * serializable specs the server reads once at world boot and turns into ordinary
 * {@link World} entities (see `server/createWorld.ts`'s `spawnNpcs`). An NPC is
 * not a special kind of entity: it is a {@link createPlayer} character with no
 * socket behind it, so it rides the snapshot/delta and renders identically to a
 * player, and a client can never drive it (no session maps any socket to it).
 *
 * Keeping the roster in `sim/` (not `server/`) keeps it unit-testable alongside
 * the rest of the simulation and honours the split where the server only
 * orchestrates content, never authors it (see ADR-0028).
 *
 * Adding an NPC is pure data - append one literal below. Speech is driven by the
 * optional {@link NpcSpec.script} field, read every tick by the pure
 * {@link stepNpcSpeech} scheduler (Slice 2); the optional {@link NpcSpec.wander}
 * field is read every tick by its sibling wander plan ({@link npcWanderPlan}, Slice 4), which
 * server-enqueues a `MoveIntent` so the NPC ambles, so the shape keeps growing
 * additively. The spawn path itself still reads only name/spawn/appearance -
 * speech and wander are both scheduled, never spawned.
 */
import { Direction } from './direction';
import { assembleEquipment } from './equipAssembly';
import type { EquippedItem } from './entity';

/**
 * One cosmetic appearance layer on an NPC: a reference into the same `equipment`
 * content registry a player picks from, plus an optional per-layer hue so the
 * layer can be tinted independently of the body (brown hair on a fair body, a
 * dyed kilt, etc.). The exact same `{ id, hue }` shape a player's
 * {@link EquippedItem} carries, minus the `layer` slot - that is resolved from
 * the registry at spawn (see {@link npcEquipment}), so the roster names a style
 * by id and never repeats the render-stack bookkeeping.
 */
export interface NpcAppearanceLayer {
  /** `equipment` registry id, e.g. `hair.long`, `beard.mustache`, `kilt.plain`. */
  id: string;
  /** 1-based UO hue id applied to this layer's art (omit / 0 = raw grayscale). */
  hue?: number;
}

/**
 * How an NPC is painted. A *struct* (not flat fields) deliberately, so richer
 * appearance slots in additively as the renderable model grows, exactly as
 * identity degrades unknown fields today. Alongside the body + hue every NPC has
 * always carried, it now names optional cosmetic layers - `hair`, `beard`,
 * `outfit` - drawn from the very same `equipment` registry players choose from
 * (Equippable Items, Slices 1-4). Each layer is pure data: the spawn path turns
 * the struct into the entity's `renderable.equipment` via {@link npcEquipment},
 * mirroring how a player's `identity.equipment` rides {@link createPlayer}, and
 * an NPC that names none still renders as a bare body + hue (graceful
 * degradation, exactly as today).
 */
export interface NpcAppearance {
  /** UO body id selecting the animation set (400 = male, 401 = female). */
  body: number;
  /** 1-based UO hue id applied to the body art. */
  hue: number;
  /** Optional hair layer (any `hair.*` id from the equipment registry). */
  hair?: NpcAppearanceLayer;
  /**
   * Optional beard layer (any `beard.*` id). Beards are gated to body 400 in the
   * registry (`bodies: [400]`); a beard named on a 401 body is dropped at spawn,
   * mirroring the player path (ADR-0031), so a female NPC never assembles one.
   */
  beard?: NpcAppearanceLayer;
  /**
   * Optional garment layer(s): one clothing id from the registry (a shirt,
   * doublet, pants, kilt, hat, sash, ...) for an NPC in a single signature piece,
   * or a list to dress one in a full outfit (e.g. olive pants + a navy shirt + a
   * feathered hat). Each id should name a *different* render layer; if two name
   * the same layer, {@link assembleEquipment} keeps the first (one item per layer).
   * A bare single `{ id, hue }` still works and stays the common case.
   */
  outfit?: NpcAppearanceLayer | NpcAppearanceLayer[];
}

/**
 * Turn an {@link NpcAppearance} struct into the validated `renderable.equipment`
 * stack the spawn path hands to {@link createPlayer} - the sim-side, content-only
 * counterpart to the server's `coerceEquipment` (`server/identity.ts`), and held
 * to the same "never throw, always yield something drawable" contract:
 *
 * - a layer whose `id` is not in the {@link equipment} registry is **dropped**
 *   (the converter never baked it), so a typo in the roster degrades to a bare
 *   body rather than a crash;
 * - a layer whose `bodies` excludes this appearance's `body` is **dropped** -
 *   notably a beard (`bodies: [400]`) named on a 401 body, mirroring the
 *   render-stack gender gate;
 * - a per-layer `hue` is kept only when it is a positive integer, else the layer
 *   renders in raw grayscale (the same clamp `coerceHue` applies);
 * - survivors are emitted in the registry's canonical layer order, so an
 *   unchanged appearance is byte-stable at spawn and never spuriously marks an
 *   entity `changed` in the delta.
 *
 * The `layer` slot each survivor carries is resolved from the registry, not the
 * roster, so the roster only ever names a style by id and hue.
 */
export function npcEquipment(appearance: NpcAppearance): EquippedItem[] {
  const outfit =
    appearance.outfit === undefined
      ? []
      : Array.isArray(appearance.outfit)
        ? appearance.outfit
        : [appearance.outfit];
  const named = [appearance.hair, appearance.beard, ...outfit].filter(
    (layer): layer is NpcAppearanceLayer => layer !== undefined,
  );
  return assembleEquipment(named, appearance.body);
}

/**
 * How an NPC talks: the pool of one-liners it shouts and the cadence it shouts
 * them on. Consumed every tick by the pure {@link stepNpcSpeech} scheduler, which
 * turns it into off-snapshot {@link SpeechMessage}s broadcast server-side (the
 * server fabricates the `Speech` stamped with the NPC's entity, never a faked
 * client `Say` - see ADR-0010 and ADR-0012).
 */
export interface NpcScript {
  /** Bark pool the NPC draws a line from (picked via the world's seeded RNG). */
  lines: string[];
  /** Base cadence between barks, in ticks (20 ticks = 1s). */
  intervalTicks: number;
  /**
   * Optional 1-based UO hue the NPC's bark text renders in, independent of its
   * (skin-toned) body hue. Each bank-sitter wears a natural skin tone now, so the
   * crowd reads as distinct people through its **speech colour** rather than oddly
   * tinted skin: {@link stepNpcSpeech} carries it on the {@link NpcSpeechEvent} and
   * the server stamps it onto the bark's `Speech`. Omit (or `0`) to fall back to
   * the speaker's body hue - the historical "NPC text = body colour" behaviour.
   */
  speechHue?: number;
  /**
   * Optional upper bound (in ticks) on a per-NPC, seeded timing spread folded
   * into {@link intervalTicks}. It gives each NPC its own period and phase so a
   * roster of bank-sitters never shouts in lockstep. Omit (or `0`) for an exact,
   * metronomic {@link intervalTicks}.
   */
  jitterTicks?: number;
  /**
   * Optional floor (in ticks) before this NPC's first bark: {@link stepNpcSpeech}
   * emits no line while the tick count is below it, regardless of the seeded
   * phase. It turns the "first bark lands a beat in, never on boot" property (see
   * the {@link NPC_ROSTER} cadence note) into an *explicit* guarantee rather than
   * a consequence of the seeded phase happening to land late for a given roster
   * index - load-bearing for the server speech tests, which assert the first
   * `Speech` a fresh client sees is the player's own `Say` (an NPC barking in the
   * first ~1s would race it). Use it when an NPC's seeded phase would otherwise
   * force an early first bark - e.g. a frequent crier on a short period. Omit (or
   * `0`) to keep the pure seeded-cadence behaviour.
   */
  warmupTicks?: number;
  /**
   * Reserved for a later slice: an ordered, multi-line scripted bit (a beggar's
   * pitch) with a per-line delay. Declared now so the shape is forward-compatible;
   * unread by today's scheduler, which barks randomly from {@link lines}.
   */
  sequence?: { text: string; afterTicks: number }[];
}

/**
 * A single NPC: an overhead name, a fixed spawn tile + facing, and an
 * appearance. `script` makes it talk (omit = silent); `wander` makes it amble
 * (omit = stands still).
 */
export interface NpcSpec {
  /** The overhead display name; sanitized at spawn like a player's name. */
  name: string;
  /** The fixed walkable tile (and facing) the NPC stands at. */
  spawn: { x: number; y: number; facing: Direction };
  /** How the NPC is drawn (body + hue today; more later). */
  appearance: NpcAppearance;
  /** The NPC's bark pool + cadence; omit to leave it silent. */
  script?: NpcScript;
  /**
   * Idle wander (Slice 4): when present, the pure shared wander brain ({@link npcWanderPlan})
   * occasionally server-enqueues a `MoveIntent` (the *same* command a player's
   * input produces) so the NPC ambles a few tiles and returns instead of standing
   * frozen, keeping within `radius` tiles (Chebyshev) of its `spawn`. Omit and the
   * NPC stands still at its spawn (the default). The wander never enters a blocked
   * tile or clips the bank geometry - movement's `isWalkable` gate handles that for
   * free - so even a generous `radius` only ambles across the open floor near the
   * spawn.
   */
  wander?: { radius: number };
}

/**
 * The bank's NPC roster: a small varied crowd of resident bank-sitters that
 * makes the West Britain Bank *feel alive* with no real players connected
 * (Slice 3). Every entry is a complete, drawable person. The starter cast is
 * deliberately small - two bankers inside the south hall (around bank-local
 * `(16, 21)`) so a visitor stepping in from the plaza is greeted by staff, and a
 * lone town crier out in the street/plaza south of the carved entrance so the
 * world reads as populated beyond the bank itself. More bank-sitters land later;
 * the shape below is pure data, so growing the crowd is just appending literals.
 *
 * The crowd reads as distinct people, not echoes, on three axes - all pure data:
 *
 * - **Appearance.** A mix of bodies (400 male / 401 female), each in a natural
 *   **skin tone** from the creation palette (`SKIN_SWATCHES` in `src/sim/skinTones.ts`:
 *   1002/1018/1023/1042/1058), never `1009` or `content.hues.get('npc')` (= 2) -
 *   both are PLAYER_HUES the server cycles per connection, so sourcing them would
 *   make a bank-sitter share a colour with a cycled player. The crowd no longer
 *   reads as distinct people through oddly tinted skin; that job moves to each
 *   NPC's **speech colour** (`script.speechHue`, below). On top of the body, most
 *   NPCs name cosmetic `hair`/`beard`/`outfit` layers (Slice 5) drawn from the
 *   same `equipment` registry players pick from - pure data the spawn path turns
 *   into `renderable.equipment` via {@link npcEquipment}. Layers are optional:
 *   omit them (as the model already degrades) and the NPC renders as a bare body +
 *   skin, so adding a styled NPC is still only editing this roster.
 * - **Voice.** Each NPC draws from its own `lines` pool keyed to a personality -
 *   the all-round greeter-banker, the brisk teller, the welcoming town crier - so
 *   the soundscape is a small varied conversation rather than one barker repeated.
 * - **Cadence.** Each carries its own `intervalTicks`/`jitterTicks`, which the
 *   seeded {@link stepNpcSpeech} scheduler turns into a distinct per-NPC period
 *   *and* phase. Distinct periods (and phases) mean the bank-sitters never bark
 *   in lockstep; they only ever fully realign every `LCM(periods)` ticks, far
 *   apart for the values in play. The cadences are also tuned so every NPC's
 *   first bark lands a couple of seconds in, never on boot - the staggered
 *   chatter ramps up rather than erupting in unison the instant a client lands.
 *   That late-first-bark property is load-bearing for the server tests: the
 *   player-`Say` tests in `gameServer.test.ts` run on the real clock and assert
 *   the *first* `Speech` a client sees is the player's own, so an NPC that barks
 *   within the first ~1s would make them flaky. When adding an NPC keep its first
 *   bark a beat late (the seeded phase from `jitterTicks` lands high for some
 *   roster indices, forcing an early first bark unless `jitterTicks` is `0`).
 *
 * Adding or removing a bank-sitter is *pure data*: append or delete one literal
 * below. Nothing else changes - the server spawns from this list generically
 * (`createWorld.ts`'s `spawnNpcs`) and the bark scheduler drives it generically
 * (`gameServer.ts` -> {@link stepNpcSpeech}), both by roster position alone.
 */
export const NPC_ROSTER: readonly NpcSpec[] = [
  {
    name: 'Banker Tobias',
    spawn: { x: 18, y: 20, facing: Direction.S },
    // A natural tan skin tone (1018), off the cycled PLAYER_HUES. Now dressed as a
    // plausible townsman-banker in the Town Crier's muted register: natural dark-
    // brown hair + a full beard (1144, off the same hair band as the crier's
    // natural 1147, never the old near-white 1109), an olive doublet over olive
    // long pants and matching boots so he is properly clothed top-to-toe (the old
    // bright doublet 1175 and missing lower garment read as odd/half-dressed). All
    // three garment dyes are real `hues.mul` ramps: 759/765 sit in the 757-766
    // olive-green ramp the crier draws from. His own colour still lives in his
    // bright-blue speech, not his skin. He now ambles the south hall (`wander`).
    appearance: {
      body: 400,
      hue: 1018,
      hair: { id: 'hair.short', hue: 1144 }, // natural dark brown (hair band, like crier's 1147)
      beard: { id: 'beard.long', hue: 1144 },
      outfit: [
        { id: 'doublet.plain', hue: 759 }, // olive (757-766 olive-green ramp)
        { id: 'pants.long', hue: 765 }, // deeper olive (same ramp) - the added lower garment
        { id: 'boots.plain', hue: 765 }, // matching olive boots
      ],
    },
    // A modest amble inside the south bank hall: radius 2 (Chebyshev) keeps him a
    // couple of tiles off his spawn (18,20) and well clear of the carved south
    // entrance (bank-local y=25), so he never drifts out into the plaza; the
    // wander scheduler's door-aware walkability gate handles the walls for free.
    wander: { radius: 2 },
    // The all-rounder: the perennial "Bank!"/"Vendor!" shouts, a WTS hawk, and a
    // bit of begging. ~4-7s between lines (80 ticks + up to 60 ticks of seeded
    // jitter at 20Hz) so it reads as idle chatter.
    script: {
      lines: [
        'Bank!',
        'Vendor!',
        'WTS full plate set, 5k gold - cheap!',
        'Spare a few gold for a weary traveller?',
        'Guards! ... where are the guards?',
        'Well met, friend!',
      ],
      intervalTicks: 80,
      jitterTicks: 60,
      speechHue: 1173, // bright blue
    },
  },
  {
    name: 'Banker Isolde',
    spawn: { x: 14, y: 22, facing: Direction.N },
    // The second teller, body 401 (female) in a pale skin tone (1002). Restyled to
    // read as normal townsfolk alongside Tobias and the crier: natural dark-brown
    // hair (1148, off the same hair band as the crier's 1147 - distinct from
    // Tobias's 1144 so the two bankers are not identical, and never the old odd
    // 1153 dye), a muted-blue fancy shirt over an olive kilt and matching shoes so
    // she has a proper lower garment (the old single bright shirt 1157 left her
    // half-dressed). A skirt-slot kilt suits the female body and keeps her cut
    // distinct from Tobias's pants. Dyes are real ramps: 1305 is in the blues ramp
    // (1301-1330), 762 in the 757-766 olive-green ramp. No beard (dropped at spawn
    // on a 401 body anyway). Her colour lives in her bright pink speech, not skin.
    // She now ambles the south hall (`wander`).
    appearance: {
      body: 401,
      hue: 1002,
      hair: { id: 'hair.long', hue: 1148 }, // natural dark brown (hair band, distinct from Tobias's 1144)
      outfit: [
        { id: 'shirt.fancy', hue: 1305 }, // muted blue (1301-1330 blues ramp)
        { id: 'kilt.plain', hue: 762 }, // olive kilt (757-766 ramp) - the added lower garment
        { id: 'shoes.plain', hue: 762 }, // matching olive shoes
      ],
    },
    // The same modest radius-2 amble as Tobias: her spawn (14,22) plus 2 tiles
    // stays inside the south hall, short of the south entrance (bank-local y=25),
    // so she never wanders out to the plaza.
    wander: { radius: 2 },
    // The brisk teller: warm welcomes and steady bank patter, a touch slower than
    // Tobias and out of lockstep with him (period 95 + up to 40 ticks of jitter).
    script: {
      lines: [
        'Welcome to the West Britain Bank!',
        'Thy gold is safe in our vaults, friend.',
        'Deposits and withdrawals - step right up!',
        'A fine day for honest banking.',
        'Mind the ledger; I count every coin.',
        'Bank open, traveller - how may I serve?',
      ],
      intervalTicks: 95,
      jitterTicks: 40,
      speechHue: 1220, // pink
    },
  },
  {
    name: 'Town Crier',
    // The captain placed this welcoming herald at the in-game read-out (87, 108),
    // out on the south plaza below the bank. That read-out is the **absolute**
    // frame, and the roster is authored **bank-local** (translated by +MAP_PADDING
    // at spawn), so it lands here as (87-75, 108-75) = (12, 33) - the first row of
    // the playable extension just past the bank's south edge. Faces south, out into
    // the open plaza the new arrivals cross.
    spawn: { x: 12, y: 33, facing: Direction.S },
    // A full outfit (the first NPC to use a multi-id `outfit` and the new `hat`/
    // `feet`/`light` slots): an olive feathered hat, a blue fancy shirt, olive
    // pants, olive boots, and a lit candle held in hand for the proclamations -
    // hat, shirt, pants, feet and light are five different render layers, so all
    // five assemble. A natural skin tone, kept off the cycled PLAYER_HUES. The
    // olive/blue ids are real populated dyes from the cosmetic-dye band: `760` is
    // a muted olive-green from the 757-766 ramp (a truer olive than the `1159`
    // dark-yellow/mustard tones), and `897` is the blue dye - both resolve to a
    // real `hues.mul` ramp, unlike the generic green/blue stand-ins they replace.
    // The candle is left undyed so its authored wax/flame renders as baked.
    appearance: {
      body: 400,
      hue: 1023,
      hair: { id: 'hair.short', hue: 1147 },
      beard: { id: 'beard.short', hue: 1147 },
      outfit: [
        { id: 'pants.long', hue: 760 }, // olive (757-766 olive-green ramp)
        { id: 'shirt.fancy', hue: 897 }, // blue
        { id: 'hat.feathered', hue: 760 }, // olive feathered hat (new `hat` slot)
        { id: 'boots.plain', hue: 760 }, // olive boots (new `feet` slot)
        { id: 'candle.lit' }, // a lit candle in hand (new `light` slot), undyed
      ],
    },
    // The welcome barker: tells a new arrival what Banksitter *is* - sit at the
    // bank, go AFK, earn cosmetic rewards for staying. A relaxed ~5s period (90
    // ticks + up to 36 jitter = 4.5-6.3s, slowed from the old brisk ~3s) so each
    // proclamation lingers long enough to read before the next lands, but with an
    // explicit `warmupTicks` floor (45 ticks ~= 2.25s) so its first line never
    // lands in the first second and races a fresh client's own first `Say` the way
    // a short-period barker otherwise would (see the cadence note above). The
    // scheduler never picks the same line twice running, so the six proclamations
    // rotate rather than stutter.
    script: {
      lines: [
        'Welcome to Banksitter! Tarry at the bank and earn rewards just for being here!',
        'Hear ye! No questing, no grinding - simply linger and treasures shall be thine!',
        'The longer ye sit, the finer the garb ye unlock - go AFK and prosper!',
        'New here? Stand a while, watch the crowd, and watch thy rewards grow!',
        'Banksitter rewards the patient - stay logged in and collect thy due!',
        'Make thyself at home, traveller - idle gold and finery await the faithful!',
      ],
      intervalTicks: 90,
      jitterTicks: 36,
      warmupTicks: 45,
      speechHue: 1260, // amber (matches the chat picker's Amber swatch)
    },
  },
  {
    name: 'Wandering Rosalind',
    // Placed at the in-game read-out (96, 84). That read-out is the **absolute**
    // frame and the roster is authored **bank-local** (translated by +MAP_PADDING
    // at spawn), so it lands here as (96-75, 84-75) = (21, 9). Faces south.
    spawn: { x: 21, y: 9, facing: Direction.S },
    // A female body (401) in a pale skin tone (1042, off the cycled PLAYER_HUES):
    // long hair, a plain dress and sandals. Dress and feet are distinct render
    // layers, so both assemble. The pink/green dyes are real populated cosmetic
    // hues (1220 pink, 1175 from the named dye strip).
    appearance: {
      body: 401,
      hue: 1042,
      hair: { id: 'hair.long', hue: 1153 },
      outfit: [
        { id: 'dress.plain', hue: 1220 }, // a plain dress, pink
        { id: 'sandals.plain', hue: 1175 }, // sandals (feet slot)
      ],
    },
    // The wander scheduler (Slice 4) keeps her within 3 tiles (Chebyshev) of her
    // spawn, walking the same MoveIntent a player would and respecting walkability.
    wander: { radius: 3 },
    // An idle stroller's patter: a touch of weather, gossip and small talk, slower
    // than the bankers and out of lockstep with them (period 88 + up to 50 ticks of
    // jitter). Pink speech to match her dress.
    script: {
      lines: [
        'A fine day for a stroll, is it not?',
        'Mind how ye go, traveller.',
        'I do love the bustle of the bank square.',
        'Have ye seen the wares the vendors bring today?',
        'Lovely weather for a wander.',
        'Good morrow! Off to enjoy the sunshine.',
      ],
      intervalTicks: 88,
      jitterTicks: 50,
      speechHue: 1220, // pink
    },
  },
  {
    name: 'Enclosed Eustace',
    // The bank's resident madman: a ragged street-prophet who now paces the open
    // **south plaza** out beyond the bank's carved entrance, and mutters that the
    // world is being fenced in. The captain placed him at the in-game read-out
    // (102, 107); that read-out is the **absolute** frame and the roster is authored
    // **bank-local** (translated by +MAP_PADDING at spawn), so it lands here as
    // (102-75, 107-75) = (27, 32) - open plaza floor on the **east side** of the
    // approach, south of the south wall + door gap (bank-local y=25) and a couple of
    // tiles in from the east edge (bank-local x=32 is blocked). Radius-2 (Chebyshev)
    // keeps his disk in x in [25, 29] / y in [30, 34]: his northernmost reach (y=30)
    // stays five tiles south of the door so he never drifts back inside, his
    // easternmost (x=29) leaves two tiles of floor (x=30, 31) before the east-edge
    // wall, and he stays clear of the dice out west - every tile in the disk is
    // walkable (verified against the live bank map). Faces W, ranting back at the
    // bank.
    spawn: { x: 27, y: 32, facing: Direction.W },
    // A male body (400) in a natural skin tone (1058, off the cycled PLAYER_HUES,
    // and the one SKIN_SWATCH the other bank-sitters don't use). Dressed as a
    // ragged ascetic: a wild mane in the bankers' natural dark brown (1144, the same
    // hair-band hue Tobias uses, so the madman reads as one of the bank's own) over a
    // long unkempt ice-white beard (1150, the ice end of the named dye strip) and a
    // drab sackcloth robe + matching sandals (1155/1154, both darks in that same named
    // strip). Robe and feet are distinct render layers, so both assemble. Every dye is
    // a real populated cosmetic hue (1150/1154/1155 sit in COSMETIC_HUE_IDS' named dye
    // strip; 1144 is the bankers' hair-band brown). His speech now matches Tobias's
    // bright blue too, not his skin.
    appearance: {
      body: 400,
      hue: 1058,
      hair: { id: 'hair.long', hue: 1144 }, // natural dark brown, matching the bankers (Tobias's 1144)
      beard: { id: 'beard.long', hue: 1150 }, // unkempt ice-white beard (named dye strip, ice end)
      outfit: [
        { id: 'robe.plain', hue: 1155 }, // drab sackcloth robe (named dye strip, darks)
        { id: 'sandals.plain', hue: 1154 }, // worn sandals (feet slot)
      ],
    },
    // Paces his east-plaza patch: radius 2 keeps the disk on open floor, short of
    // the south door to the north and the east-edge wall, the wander gate handles
    // the walls for free.
    wander: { radius: 2 },
    // The street-prophet's enclosure rant: short paranoid mutterings on the
    // captain's "enclosure" theme. A slow, brooding cadence (period 110 + up to 45
    // ticks of jitter, ~5.5-7.75s) so he mutters rather than floods, distinct from
    // every other roster period (80/95/55/88) so he never falls into lockstep; an
    // explicit warmup floor (50 ticks ~= 2.5s) keeps his first line off the boot
    // second, like the Town Crier, so it never races a fresh client's own first
    // Say. His speech now renders in the bankers' bright blue (Tobias's 1173) so the
    // madman matches the bank's own. Lines are kept short for the 220px overhead wrap
    // (ADR-0082).
    script: {
      lines: [
        'Do you think we are enclosed?',
        'Beware the Enclosure Scams - they fence ye in, then sell ye the key!',
        'UO servers always end up enclosed.',
        'If you find a way out of the enclosure will you come back for me?',
        'The fences creep closer each night - can ye not see them?',
        'They are enclosing the whole world, tile by tile!',
        'No walls today? They raise them while ye sleep.',
      ],
      intervalTicks: 110,
      jitterTicks: 45,
      warmupTicks: 50,
      speechHue: 1173, // bright blue, matching the bankers (Tobias's 1173)
    },
  },
  {
    name: 'Lost Lachlan',
    // A displaced adventurer who clearly wandered in from some *other* game and
    // has not realised he is in a medieval bank chat room. The captain placed him
    // at the in-game read-out (75, 96); that read-out is the **absolute** frame and
    // the roster is authored **bank-local** (translated by +MAP_PADDING at spawn),
    // so it lands here as (75-75, 96-75) = (0, 21) - the westmost walkable column of
    // the bank's interior (abs x=75; the column at abs x=74 and west is the bank's
    // outer wall). Verified walkable against the live bank map: abs (75, 96) is open
    // floor in a north-south interior corridor (open abs x in [75, 81], walled at
    // x<=74 and x=82). Faces E, peering bewildered into the open hall (away from the
    // wall at his back) as he tries to get his bearings.
    spawn: { x: 0, y: 21, facing: Direction.E },
    // A male body (400) in a pale skin tone (1002, a SKIN_SWATCH off the cycled
    // PLAYER_HUES 1009/2). Per the captain's spec he is *bare* save for a single
    // garment: charcoal short pants - no shirt, no shoes, no hat, no beard. His one
    // styled flourish is his hair, set to **the bankers' natural dark brown** - the
    // same hair-band hue Banker Tobias uses (1144, the hue Enclosed Eustace was also
    // recoloured to match), so he reads as one of the others rather than a lookalike.
    // It is read from Tobias's literal value, not approximated. The pants use
    // the captain-specified charcoal hue 11175 exactly (an out-of-ramp id applied
    // verbatim; `assembleEquipment` keeps any positive-integer hue, ADR-0089).
    appearance: {
      body: 400,
      hue: 1002,
      hair: { id: 'hair.short', hue: 1144 }, // natural dark brown, matching the bankers (Tobias's 1144)
      outfit: { id: 'pants.short', hue: 11175 }, // charcoal short pants only (captain-specified hue 11175)
    },
    // A modest amble fitting a man who thinks he is lost: radius 2 (Chebyshev), the
    // same modest reach as the bankers and Eustace. The disk (abs x in [73, 77], y in
    // [94, 98]) overhangs the west wall (abs x=73, 74 are blocked), but `pickWanderStep`
    // only ever picks in-radius *walkable* tiles, so he never steps onto the wall - the
    // wander gate handles it for free, exactly as it does for Banker Tobias/Isolde. That
    // leaves him the open strip abs x in [75, 77] / y in [94, 98] to wander, well clear
    // of the doors (abs (97,100)/(98,100)) and the east wall (abs x=82).
    wander: { radius: 2 },
    // The baffled MMO-refugee's patter: he keeps trying his *old* game's mechanics
    // and narrates them failing here. Includes the captain's five signature lines
    // (Outpost, the tele rope hotkey, Prevalia, aspect armor, rainbows) plus a
    // couple more in the same register. Kept short for the 220px overhead wrap
    // (ADR-0082). A deliberately slow, sparse cadence - markedly slower than the
    // Town Crier (period 150 + up to 50 ticks of jitter, so he speaks only every
    // several seconds rather than the crier's couple), over the same explicit 45-tick
    // warmup floor so he is not spammy and his first line never races a fresh client's
    // own first Say; the per-roster-index seed still gives him a period/phase distinct
    // from the others, so they never fall into lockstep. Speech renders in
    // the project's plain **white** - hue `0`, the hue-0 default that `hueColor`
    // resolves to 0xffffff (the same white a player's untinted speech draws in) -
    // so his barks read as crisp white overhead, distinct from any dyed voice.
    script: {
      lines: [
        'Is this the way to Outpost? I swear it was right here…',
        "Why won't my tele rope hotkey fire?",
        'Wait — is this Prevalia?',
        "My aspect armor won't activate. Cursed lag.",
        'Rainbows are my only weakness',
        'Where is the quest log in this place?',
        'No minimap? How does anyone find their way?',
      ],
      intervalTicks: 150, // slow, sparse cadence - well above the crier's 55 so he talks less often
      jitterTicks: 50,
      warmupTicks: 45,
      speechHue: 0, // plain white (hue-0 default -> 0xffffff via hueColor)
    },
  },
];

/**
 * The set of resident NPC display names (normalized to lower-case, trimmed), derived
 * once from {@link NPC_ROSTER}. A socket-less roster NPC renders identically to a real
 * player - the server tells them apart by whether a connection maps to the entity
 * ({@link server/gameServer}'s `connectionForEntity`), which the client never sees. So a
 * client that needs a best-effort player-vs-NPC read (e.g. gating the right-click "Roll
 * the dice" / "Duel" match options to real players, ADR-0160) matches the target's name
 * against this set. It is authoritative for the fixed roster; the server still rules on
 * every challenge (`is-npc`), so a rare name collision only mis-offers a menu item the
 * server then refuses.
 */
const NPC_NAMES: ReadonlySet<string> = new Set(
  NPC_ROSTER.map((npc) => npc.name.trim().toLowerCase()),
);

/**
 * Whether `name` belongs to a resident bank NPC (see {@link NPC_NAMES}). Trims and
 * lower-cases before matching, so casing/whitespace never misses. An empty/undefined
 * name is not an NPC.
 */
export function isNpcName(name: string | undefined): boolean {
  if (!name) return false;
  return NPC_NAMES.has(name.trim().toLowerCase());
}
